import React from 'react';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { getFormattedDateTime, getFormattedDuration, SETTINGS } from '../App';
import { CircularProgress, Typography, TextField } from "@mui/material";
import { QuickSearchToolbar } from './QuickSearchBox';


type ResponseObj = Array<MarsClass>

export type MarsClass = {
  path: string;
  description: string;
  id_schema: string;
  shared_namespace: string | null;
  locked: boolean;
}

type Row = {
  id: string;
  description: string;
  id_schema: string;
  shared_namespace: string | null;
  locked: boolean;
}

const fetchSessions = (): Promise<ResponseObj> => {
  return fetch(SETTINGS.url.marsclasses)
    .then((res) => res.json());
}

export const sessionsColumnsDefinition: GridColDef[] = [
  {
    field: 'id',
    headerName: 'Mars Class Path',
    width: 225,
    type: 'number',
    hideable: false,
    cellClassName: "monospaced-text"
  },
  {
    field: 'description',
    headerName: 'Description',
    width: 200,
    flex: 1,
    hideable: false
  },
  {
    field: 'id_schema',
    headerName: 'Schema',
    minWidth: 200,
    flex: 0,
    hideable: false
  },
  {
    field: 'shared_namespace',
    headerName: 'Shared Namespace',
    minWidth: 200,
    flex: 0,
    hideable: true,
    hide: true
  },
  {
    field: 'locked',
    headerName: 'Locked',
    minWidth: 100,
    flex: 0,
    hideable: true,
    hide: true
  }
];


export default function MarsClasses() {

  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [allData, setAllData] = useState<Array<Row>>([]); // store all data
  const [searchText, setSearchText] = useState<string>("");
  const [hasError, setHasError] = useState<string | null>(null);
  const [currentNid, setCurrentNid] = useState<string | null>(null);

  function sessionPopUp(e: GridRowParams<Row>) {
    setCurrentNid(e.row.id);
  }

  const getData = () => {
    setIsDataLoading(true);

    fetchSessions()
      .then((res) => {
        const rows = res.map((item) => {
          return {
            id: item['path'],
            description: item['description'],
            id_schema: item['id_schema'],
            shared_namespace: item['shared_namespace'],
            locked: item['locked']
          }
        });
        setAllData(rows);
      })
      .catch((error: any) => {
        setHasError(error.message);
      })
      .finally(() => {
        setIsDataLoading(false);
      })
  }

  useEffect(() => {
    getData();
    const intervalId = setInterval(getData, 30 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Filtered data is always derived from allData and searchText
  const loadedData = React.useMemo(() => {
    if (!searchText) return allData;
    const lower = searchText.toLowerCase();
    return allData.filter(row =>
      row.id.toLowerCase().includes(lower) ||
      row.description.toLowerCase().includes(lower) ||
      row.shared_namespace?.toLowerCase().includes(lower)
    );
  }, [allData, searchText]);

  return (
    <Box>
      <h2 className='section-header'>Mars Classes</h2>

      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ minWidth: 250, mb: 1 }}>
            <QuickSearchToolbar searchText={searchText} setSearchText={setSearchText} />
          </Box>
          <Box sx={{ height: 533, width: '100%' }}>

            {
              isDataLoading && !loadedData &&
              <CircularProgress sx={{ display: "block", margin: "10px auto" }} />
            }

            {
              hasError &&
              <Box>
                <Typography color="error">
                  Error fetching Mars Classes!
                </Typography>
              </Box>
            }


            {
              !hasError && loadedData &&
              <DataGrid
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'id', sort: 'desc' }],
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ p: 2 }}>
                      No Mars Classes found.
                    </Box>
                  )
                }}

                rows={loadedData}
                columns={sessionsColumnsDefinition}
                rowHeight={42}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
                getRowClassName={(params: { row: Row }) => ""}
              />
            }
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
